// src/pages/ProductDetailPage.jsx
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { getProduct, getItemsByVendorId, getSubcategoriesByVendorId } from '../firebase/services';
import { useCart } from '../context/CartContext';
import Toast from '../components/Toast';
import './ProductDetail.css';

// Spinner Component
const Spinner = () => (
  <div style={{ position: 'fixed', inset: 0, background: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 999 }}>
    <div className="spinner"></div>
  </div>
);

// Memoized Card Component
const ProductCard = React.memo(({ item, onAdd }) => {
  return (
    <div className="product-card">
      <div className="product-img-wrapper">
        {item.ImageBlob || item.image ? (
          <img 
            src={item.ImageBlob || item.image} 
            alt={item.ItemName || item.name} 
            className="product-img" 
            loading="lazy"
          />
        ) : (
          <div className="product-img" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 32 }}>🍽</div>
        )}
        
        {item.isVeg !== undefined && (
          <div className="product-badge">
            {item.isVeg ? '🟢 Veg' : '🔴 Non-Veg'}
          </div>
        )}
      </div>

      <div className="product-content">
        <h3 className="product-name">{item.ItemName || item.name}</h3>
        <span className="product-price">₹{item.TotalPrice || item.ItemPrice || item.price}</span>
        
        {item.ItemDescription && (
          <p className="product-desc">{item.ItemDescription}</p>
        )}

        <button 
          onClick={() => onAdd(item)}
          className="add-btn"
        >
          + Add to Bag
        </button>
      </div>
    </div>
  );
});

export default function ProductDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { addToCart } = useCart();
  
  const [initialProduct, setInitialProduct] = useState(null);
  const [vendorItems, setVendorItems] = useState([]);
  const [vendorCategories, setVendorCategories] = useState([]);
  const [activeCategory, setActiveCategory] = useState('all');
  const [loading, setLoading] = useState(true);
  const [toast, setToast] = useState(null);

  useEffect(() => {
    let isMounted = true;
    setLoading(true);

    async function loadData() {
      try {
        // 1. Get the clicked product to identify the Vendor
        const prod = await getProduct(id);
        if (!prod) { navigate('/shop'); return; }
        if (!isMounted) return;
        
        setInitialProduct(prod);
        
        // 2. Fetch Vendor's Categories and Items in Parallel
        const [cats, items] = await Promise.all([
          getSubcategoriesByVendorId(prod.vendorId),
          getItemsByVendorId(prod.vendorId)
        ]);

        if (!isMounted) return;
        
        setVendorCategories(cats || []);
        setVendorItems(items || []);
        
        // 3. Set active category to the product's category
        if (prod.SubCategoryId) {
          setActiveCategory(prod.SubCategoryId);
        } else {
          setActiveCategory('all');
        }

      } catch (err) {
        console.error(err);
        navigate('/shop');
      } finally {
        if (isMounted) setLoading(false);
      }
    }

    loadData();
    return () => { isMounted = false; };
  }, [id, navigate]);

  const filteredItems = useMemo(() => {
    if (activeCategory === 'all') return vendorItems;
    return vendorItems.filter(item => item.SubCategoryId === activeCategory);
  }, [vendorItems, activeCategory]);

  const handleAddToCart = useCallback((item) => {
    addToCart({
      id: item.id,
      name: item.ItemName || item.name,
      price: item.TotalPrice || item.ItemPrice || item.price,
      image: item.ImageBlob || item.image || '',
      type: 'food',
      vendorName: initialProduct?.vendorName,
      vendorId: initialProduct?.vendorId,
    });
    setToast({ message: `${item.ItemName || item.name} added to bag!`, type: 'success' });
  }, [addToCart, initialProduct]);

  if (loading) return <Spinner />;

  return (
    <div className="detail-page-container">
      {toast && <Toast {...toast} onClose={() => setToast(null)} />}

      <div className="detail-layout">
        {/* ── LEFT SIDEBAR ── */}
        <aside>
          <div className="sidebar-box">
            <div className="sidebar-title">Categories</div>
            
            <div>
              <button 
                onClick={() => setActiveCategory('all')}
                className={`cat-btn ${activeCategory === 'all' ? 'active' : ''}`}
              >
                <span className="cat-icon">🍽</span>
                <span>All Items</span>
              </button>

              {vendorCategories.map(cat => (
                <button 
                  key={cat.id} 
                  onClick={() => setActiveCategory(cat.id)}
                  className={`cat-btn ${activeCategory === cat.id ? 'active' : ''}`}
                >
                  {cat.imageBlob ? (
                    <img src={cat.imageBlob} className="cat-icon" alt={cat.name} />
                  ) : (
                    <span className="cat-icon">🍽</span>
                  )}
                  <span>{cat.name}</span>
                </button>
              ))}
            </div>
          </div>
        </aside>

        {/* ── MAIN CONTENT ── */}
        <div>
          {/* Header */}
          <div style={{ marginBottom: 30, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <div>
              <h1 style={{ fontFamily: 'Playfair Display, serif', fontSize: 28, color: '#253d4e', margin: 0 }}>
                {initialProduct?.vendorName || 'Menu'}
              </h1>
              <p style={{ fontSize: 13, color: '#888', marginTop: 5 }}>{filteredItems.length} items found</p>
            </div>
            <Link to="/shop" className="btn btn-outline" style={{ padding: '8px 20px', fontSize: 12 }}>
              ← Back to Shops
            </Link>
          </div>

          {filteredItems.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '80px 0', color: '#888' }}>
              <div style={{ fontSize: 48, marginBottom: 16 }}>🍽</div>
              <p>No items found.</p>
            </div>
          ) : (
            <div className="items-grid">
              {filteredItems.map(item => (
                <ProductCard 
                  key={item.id} 
                  item={item} 
                  onAdd={handleAddToCart} 
                />
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}